import cn from 'classnames'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { QualityGauge } from 'partner360/components/qualityIndicator'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../../configs/translations'
import { formatCockpitScore } from '../../../../../../lib'
import type { ICockpitPlanningStatus } from '../../../../../../model/hooks'
import { useCountUp } from '../../hooks/useCountUp'

import styles from './PlanningStatusBlocks.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planDeck
const STATUS_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planningStatus

const EMPTY_VALUE = '--'

const totalParam = (total: number) => [{ key: '{total}', value: String(total) }]

interface IBlockProps {
  caption: string
  value: ReactNode
  /** The line under the value: the completeness bar, the signature's date, or nothing, kept at
   *  one height so the three values stand on one baseline. */
  foot?: ReactNode
  /** `data-tour-id` for the block, from the cockpit whose tour points at it. */
  tourId?: string
  /** Opens what stands behind the figure: the caption becomes a link to it, underlined. */
  onCaptionClick?: () => void
  /** The caption opens a popover, open now. */
  expanded?: boolean
}

const Block = ({ caption, value, foot, tourId, onCaptionClick, expanded }: IBlockProps) => (
  <div className={styles.block} data-tour-id={tourId}>
    {onCaptionClick ? (
      <button
        type="button"
        className={cn(styles.caption, styles.captionLink, { [styles.captionLinkOpen]: expanded })}
        aria-expanded={expanded}
        onClick={onCaptionClick}
      >
        <Translation text={caption} span />
      </button>
    ) : (
      <span className={styles.caption}>
        <Translation text={caption} span />
      </span>
    )}
    <span className={styles.value}>{value}</span>
    <span className={styles.foot}>{foot}</span>
  </div>
)

/** The `data-tour-id`s of the three blocks. Both cockpits render them and each has a tour of its
 *  own, so each passes its own ids. */
export type TPlanningStatusBlocksTourTargets = Record<
  'statusCompleteness' | 'statusAcknowledgment' | 'statusPlanQuality',
  string
>

interface IPlanningStatusBlocksProps {
  status: ICockpitPlanningStatus
  /** The figures count up, the needle swings and the blocks rise in turn as they appear. */
  animated?: boolean
  tourTargets?: TPlanningStatusBlocksTourTargets
  /** Opens what stands behind completeness: the caption is then a link to it. */
  onCompletenessClick?: () => void
  /** What the completeness caption opens is open, said to assistive technology. */
  isCompletenessOpen?: boolean
  /** Opens what stands behind acknowledgment: the caption is then a link to it. */
  onAcknowledgmentClick?: () => void
  /** Opens the plan quality review from the caption of a scored plan. */
  onQualityClick?: () => void
}

/** The selected plan's state in large figures: how complete it is, whether it is signed and its
 *  plan quality. `Overall` counts the complete and the signed offerings instead. */
export const PlanningStatusBlocks = ({
  status,
  animated = false,
  tourTargets,
  onCompletenessClick,
  isCompletenessOpen,
  onAcknowledgmentClick,
  onQualityClick,
}: IPlanningStatusBlocksProps) => {
  const { completeness, acknowledgment, score } = status
  const isOverall = 'count' in completeness
  const percent = useCountUp('percent' in completeness ? completeness.percent : null, animated)
  const shownScore = useCountUp(score, animated)

  const completenessRatio =
    'percent' in completeness
      ? (completeness.percent ?? 0) / 100
      : completeness.total > 0
        ? completeness.count / completeness.total
        : 0

  const completenessValue =
    'percent' in completeness ? (
      completeness.percent === null ? (
        <strong className={styles.big}>{EMPTY_VALUE}</strong>
      ) : (
        <>
          <strong className={styles.big}>{`${Math.round(percent ?? 0)}%`}</strong>
          <span className={styles.word}>
            <Translation text={KEYS.completed} span />
          </span>
        </>
      )
    ) : completeness.total === 0 ? (
      <strong className={styles.big}>{EMPTY_VALUE}</strong>
    ) : (
      <>
        <strong className={styles.big}>{completeness.count}</strong>
        <span className={styles.word}>
          <Translation
            text={KEYS.ofCompleted}
            replaceParams={totalParam(completeness.total)}
            span
          />
        </span>
      </>
    )

  const acknowledgmentValue =
    'count' in acknowledgment ? (
      acknowledgment.total === 0 ? (
        <strong className={styles.big}>{EMPTY_VALUE}</strong>
      ) : (
        <>
          <strong className={styles.big}>{acknowledgment.count}</strong>
          <span className={styles.word}>
            <Translation
              text={KEYS.ofAcknowledged}
              replaceParams={totalParam(acknowledgment.total)}
              span
            />
          </span>
        </>
      )
    ) : acknowledgment.acknowledged === null ? (
      <strong className={styles.big}>{EMPTY_VALUE}</strong>
    ) : (
      <span className={styles.mid}>
        <Translation
          text={acknowledgment.acknowledged ? KEYS.acknowledged : STATUS_KEYS.notAcknowledged}
          span
        />
      </span>
    )

  const acknowledgmentDate =
    'acknowledged' in acknowledgment && acknowledgment.acknowledged && acknowledgment.date ? (
      <span className={styles.date}>
        <Translation
          text={KEYS.acknowledgedOn}
          replaceParams={[{ key: '{date}', value: acknowledgment.date }]}
          span
        />
      </span>
    ) : null

  return (
    <div className={cn(styles.root, { [styles.animated]: animated })}>
      <Block
        caption={KEYS.completeness}
        value={completenessValue}
        foot={
          <span
            className={cn(styles.bar, { [styles.barDone]: completenessRatio >= 1 })}
            aria-hidden="true"
          >
            <i style={{ width: `${Math.min(100, Math.round(completenessRatio * 100))}%` }} />
          </span>
        }
        tourId={tourTargets?.statusCompleteness}
        onCaptionClick={onCompletenessClick}
        expanded={onCompletenessClick ? Boolean(isCompletenessOpen) : undefined}
      />
      <Block
        caption={KEYS.acknowledgment}
        value={acknowledgmentValue}
        foot={acknowledgmentDate}
        tourId={tourTargets?.statusAcknowledgment}
        onCaptionClick={onAcknowledgmentClick}
      />
      <Block
        caption={isOverall ? KEYS.averagePlanQuality : KEYS.planQuality}
        value={
          <>
            {/* With no score the needle has nothing to point at, so only the dash is shown. */}
            {score !== null && <QualityGauge score={score} height={24} animated={animated} />}
            <strong className={styles.big}>{formatCockpitScore(shownScore)}</strong>
          </>
        }
        tourId={tourTargets?.statusPlanQuality}
        onCaptionClick={score !== null ? onQualityClick : undefined}
      />
    </div>
  )
}

PlanningStatusBlocks.displayName = 'PlanningStatusBlocks'
